import React, { useState, useMemo, useEffect, useImperativeHandle, forwardRef, useCallback, useRef } from 'react';
import PropTypes, { array, string, bool, number, func, object } from 'prop-types';
import { useTable, useGlobalFilter, useSortBy, usePagination, useExpanded } from 'react-table';

import { OpenObjectURL } from '../../../utils/helpers';
import { ALERT_TYPE, ShowToastMessage, TextEllipsis, buildAvmrColumn, buildCheckColumn } from '@laramie/ui';
import { useAccess } from '../../hooks/useAccess';
import Select from '../Select';

import './index.scss';


const NON_TEXT_COLUMN_IDS = ['select', 'abm', 'expnader'];

const TableCustom = forwardRef(function TableCustom(props, ref) {

    const [state, setState] = useState({ globalFilter: props.defaultGlobalFilter ?? "" });

    useEffect(() => { setGlobalFilter(state.globalFilter); }, [props.data]);

    const [showSelectionMenu, setShowSelectionMenu] = useState(false);
    const selectionMenuRef = useRef(null);
    const prevDataSignatureRef = useRef(null);

    const [hasEditAccess, setHasEditAccess] = useState(props.editCode ? false : true);
    useAccess({
        key: 'TableCustom',
        onLoaded: (data, isSuccess, error) => {
            if (!isSuccess) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error)
            else if (props.editCode) setHasEditAccess(data.some(x => x.codigo === props.editCode))
        }
    });

    const rt_columns = useMemo(() => {
        let cols = props.columns
        if (props.useSelectedField)
            cols = [buildCheckColumn(props), ...props.columns]
        if (props.avmr)
            cols = [...cols, buildAvmrColumn(props.avmr, hasEditAccess && !props.disabled)]

        return cols.map((col, index) => ({
            ...col, id: col.id || `col_${index}`,
        }))
    }, [props.columns, hasEditAccess])
    const rt_data = useMemo(() => props.data, [props.data]);

    const normalizeString = (str) => String(str).replace(/\s+/g, ' ').trim().toLowerCase();

    const globalFilterFunction = useCallback((rows, columnIds, filterValue) => {
        if (!filterValue || filterValue === '') return rows;
        
        const searchTerm = normalizeString(filterValue);
        
        return rows.filter(row => {
            return rt_columns.some(column => {
                let cellValue;
                
                if (column.Cell && typeof column.Cell === 'function') {
                    try {
                        const rendered = column.Cell({
                            row: { original: row.original },
                            value: row.original[column.accessor]
                        });
                        cellValue = rendered;
                    } catch {
                        cellValue = row.original[column.accessor];
                    }
                } else {
                    cellValue = row.original[column.accessor];
                }
                
                if (cellValue == null) return false;
                return normalizeString(cellValue).includes(searchTerm);
            });
        });
    }, [rt_columns]);

    const dataSignature = useMemo(() => {
        if (!props.data || props.data.length === 0) {
            return "empty";
        }
        return props.data.map(item => item.id).join(',') + '|' + props.data.length;
    }, [props.data]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        footerGroups,
        prepareRow,
        visibleColumns,
        //global filter
        setGlobalFilter,
        //pagination
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = useTable({
            columns: rt_columns,
            data: rt_data,
            autoResetExpanded: false,
            autoResetPage: false,
            autoResetSortBy: false,
            globalFilter: globalFilterFunction,
            initialState: {
                pageIndex: props.pageIndex,
                pageSize: props.pageSize
            }
        },
        useGlobalFilter,
        useSortBy,
        useExpanded,
        usePagination
    );

    useEffect(() => {
        if (typeof gotoPage !== 'function') return;
        
        if (typeof props.restoredPageIndex === 'number') {
            gotoPage(props.restoredPageIndex);
        } else {
            gotoPage(0);
        }
    }, [dataSignature, gotoPage, props.restoredPageIndex]);

    useEffect(() => {
        if (props.defaultGlobalFilter !== undefined) {
            setState({ globalFilter: props.defaultGlobalFilter });
            setGlobalFilter(props.defaultGlobalFilter);
        }
    }, [props.defaultGlobalFilter, setGlobalFilter]);

    const resetSelections = () => {
        if (prevDataSignatureRef.current !== null && prevDataSignatureRef.current !== dataSignature) {
            if (props.onItemsSelected) {
                props.onItemsSelected(props.data.map(x => ({ id: x.id, selected: false })))
            }
        }
        prevDataSignatureRef.current = dataSignature;
    }

    useEffect(resetSelections, [dataSignature])

    const selectedItems = useMemo(() => {
        return props.data.filter(item => item.selected === true);
    }, [props.data]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (selectionMenuRef.current && !selectionMenuRef.current.contains(event.target)) {
                setShowSelectionMenu(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleRemoveSelection = (itemId) => {
        props.onItemsSelected([{ id: itemId, selected: false }]);
        const remainingSelected = selectedItems.filter(item => item.id !== itemId);
        if (remainingSelected.length === 0) {
            setShowSelectionMenu(false);
        }
    };

    const handleClearAllSelections = () => {
        props.onItemsSelected(props.data.map(x => ({ id: x.id, selected: false })));
        setShowSelectionMenu(false);
    };

    useImperativeHandle(ref, () => ({
        manualReset: () => {
            gotoPage(0)
        },
        get pageIndex() {
            return typeof pageIndex === 'number' ? pageIndex : 0;
        },
        gotoPage: gotoPage,
        get globalFilter() {
            return state.globalFilter ?? '';
        },
        setGlobalFilter: (value) => {
            setState({ globalFilter: value });
            setGlobalFilter(value);
        }
    }))
    
    const getColumnSortIcon = (column) => {
        if (column.disableSortBy || !column.isSorted) return ''
        else if (column.isSortedDesc) return <span className="material-symbols-outlined float-end">expand_less</span>
        else return <span className="material-symbols-outlined float-end">expand_more</span>
    }

    const handleSortClick = (column, originalOnClick) => (e) => {
        if (!column.disableSortBy) {
            gotoPage(0);
        }
        if (originalOnClick) {
            originalOnClick(e);
        }
    }

    const onClickCSV = () => {
        const filteredColumns = rt_columns.filter(col => !['index', 'id'].includes(col.accessor))
        let csv = ''
	 

        csv += filteredColumns.map(col => `"${col.Header}"`).join(';') + '\n'
        rt_data.forEach(row => {
            const result = []
            filteredColumns.forEach(col => {
                try {
                    if (col.csvValue) result.push(col.csvValue(row))
                    else if (col.Cell) result.push(col.Cell({
                        row: {
                            original: row
                        },
                        value: row[col.accessor]
                    }))
                    else result.push(row[col.accessor])
                }
                catch {
                    result.push("ERROR");
                }
            })
            csv += result.map(value => {
                const text = value === null || value === undefined ? '' : String(value)
                return /[;,"\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
            }).join(';') + '\n'
        })
        OpenObjectURL(props.csvName, new Blob(['\ufeff' + csv], { type: 'text/csv' }))
    }

    const getCellClassName = (column, isFooter = false) => {
        let name = `${(column.alignCell && column.alignCell === 'right') ? 'align-right' :
            (column.alignCell && column.alignCell === 'center') ? 'align-center' : 'align-left'}`;
        if (column.ellipsis !== false && !isFooter && !NON_TEXT_COLUMN_IDS.includes(column.id))
            name += ' cell-ellipsis';
        if (props.onClickRow)
            name += ' clickable';
        return name
    }

    const handleClickRow = (row, cellId) => {
        if (props.onClickRow) {
            props.onClickRow(row, cellId);
        }
    }

    return (

        <div className={`${props.className}${props.floatingDownloadCSVButton ? ' TableCustomFloatingDownloadCSVButton' : ''}`}>

            {(props.showFilterGlobal || props.showDownloadCSV) && rt_data.length > 0 && (
                <div className='rt-utilities'>
                    {props.showFilterGlobal && (
                        <div className='rt-globalFilter'>
                            <input
                                className='form-control'
                                placeholder='Buscar...'
                                value={state.globalFilter}
                                onChange={e => {
                                    const newValue = e.target.value;
                                    setState({ globalFilter: newValue });
                                    setGlobalFilter(newValue);
                                    if (typeof gotoPage === 'function') {
                                        gotoPage(0);
                                    }
                                }}
                            />
                        </div>
                    )}

                    {props.showDownloadCSV && 
                        <div className="rt-download link" onClick={ (event) => onClickCSV() }>
                            <span className="material-symbols-outlined" title="Descargar CSV">download</span>
                        </div>
                    }
                </div>
            )}

            <table {...getTableProps()}>
                {props.showHeader &&
                <thead>
                    {headerGroups.map(headerGroup => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map(column => {
                            const sortByProps = column.getSortByToggleProps();
                            const sortable = column.canSort && page.length > 0;
                            return (
                            <th
                                {...column.getHeaderProps([sortByProps, { style: { width: column.width, cursor: sortable ? 'pointer' : 'default' }, title: ''}])}
                                onClick={sortable ? handleSortClick(column, sortByProps.onClick) : undefined}
                                className={column.ellipsis !== false ? 'cell-ellipsis min-column' : ''}
                            >
                                <div className='table-custom-header-container'>
                                    <TextEllipsis tooltip={false}>{column.render('Header')}</TextEllipsis>
                                    {getColumnSortIcon(column)}
                                </div>
                            </th>
                        )})}
                    </tr>
                    ))}
                </thead>
                }
                <tbody {...getTableBodyProps()}>
                    {page.map((row, i) => {
                    prepareRow(row)
                    return (
                        <React.Fragment key={i}>
                            <tr className={`rt-tr-row${props.getRowClassName ? ` ${props.getRowClassName(row.original)}` : ''}`} {...row.getRowProps()}>
                            {row.cells.map(cell => {
                                return (
                                <td
                                    {...cell.getCellProps([{ style: { width: cell.column.width }}])}
                                    className={getCellClassName(cell.column)}
                                    onClick={() => handleClickRow(row, cell.column.id)}
                                >
                                    {NON_TEXT_COLUMN_IDS.includes(cell.column.id)
                                        ? cell.render('Cell')
                                        : <TextEllipsis>{cell.render('Cell')}</TextEllipsis>}
                                </td>
                                )
                            })}
                            </tr>
                            {row.isExpanded ? (
                                <tr className='rt-tr-expanded'>
                                    <td colSpan={visibleColumns.length}>
                                        {props.subComponent({ row })}
                                    </td>
                                </tr>
                            ) : null}
                        </React.Fragment>
                    )
                    })}
                    {page.length === 0 ? (
                        <tr>
                            <td colSpan={visibleColumns.length} className="empty-row">
                            {props.messageEmpty}
                            </td>
                        </tr>
                    ) : null}
                </tbody>
                {props.showFooter &&
                <tfoot>
                    {footerGroups.map(footerGroup => (
                    <tr {...footerGroup.getFooterGroupProps()}>
                        {footerGroup.headers.map(column => (
                        <td {...column.getFooterProps()} className={getCellClassName(column, true)}>
                            {column.render('Footer')}
                        </td>
                        ))}
                    </tr>
                    ))}
                </tfoot>
                }
            </table>

            {(rt_data.length > props.pageSize || pageSize > props.pageSize) && page.length > 0 && (
            <div className='rt-page'>

                {props.useSelectedField && selectedItems.length > 0 && (
                    <div className="rt-selection-panel" ref={selectionMenuRef}>
                        <div 
                            className="rt-selection-toggle"
                            onClick={() => setShowSelectionMenu(!showSelectionMenu)}
                        >
                            <span>{selectedItems.length} {selectedItems.length === 1 ? 'seleccionado' : 'seleccionados'}</span>
                            <span className="material-symbols-outlined rt-selection-chevron">
                                {showSelectionMenu ? 'expand_less' : 'expand_more'}
                            </span>
                        </div>
                        
                        {showSelectionMenu && (
                            <div className="rt-selection-menu">
                                <div className="rt-selection-list">
                                    {selectedItems.map((item) => (
                                        <div key={item.id} className="rt-selection-item">
                                            <span className="rt-selection-item-text">
                                                {typeof props.selectionLabelAccessor === 'function'
                                                    ? props.selectionLabelAccessor(item)
                                                    : props.selectionLabelAccessor 
                                                        ? item[props.selectionLabelAccessor] 
                                                        : item.nombre || item.descripcion || item.id}
                                            </span>
                                            <span 
                                                className="material-symbols-outlined rt-selection-remove"
                                                onClick={() => handleRemoveSelection(item.id)}
                                            >
                                                close
                                            </span>
                                        </div>
                                    ))}
                                </div>
                                <div className="rt-selection-divider"></div>
                                <div className="rt-selection-clear">
                                    <span onClick={handleClearAllSelections}>Limpiar todo</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="rt-page-right">
                    {props.showPageSize && (
                    <div className='rt-pagesize'>
                        <Select
                            name="pageSize"
                            className='form-control'
                            value={pageSize}
                            onChange={({ target }) => {
                                setPageSize(Number(target.value))
                            }}
                            options={[
                                {key: 5, value: '5'},
                                {key: 10, value: '10'},
                                {key: 25, value: '25'},
                                {key: 50, value: '50'},
                                {key: rt_data.length, value: 'todos'}
                            ].filter((item, index, items) => items.findIndex(i => i.key === item.key) === index)
                             .map((item) => ({ value: item.key, label: `Mostrar ${item.value} registros` }))}
                        />
                    </div>
                    )}

                    {props.showPageSize && (!props.showPageCountOnlyPage || pageOptions.length > 1) && (
                        <div className="rt-page-separator"></div>
                    )}

                    {(!props.showPageCountOnlyPage || pageOptions.length > 1) && (
                    <div className='rt-pagecount'>
                        <div onClick={() => (canPreviousPage) ? gotoPage(0) : null } className="link p-right-10">
                            <span className="material-symbols-outlined" style={(!canPreviousPage) ? {opacity:0.8} : {}}>keyboard_double_arrow_left</span>
                        </div>

                        <div onClick={() => (canPreviousPage) ? previousPage() : null} className="link p-right-10">
                            <span className="material-symbols-outlined" style={(!canPreviousPage) ? {opacity:0.8} : {}}>chevron_left</span>
                        </div>

                        <div className='rt-pagecount-current pagecount-text p-right-5'>
                            <input
                                className='form-control'
                                type="text"
                                value={pageIndex + 1}
                                min={1}
                                max={pageCount}
                                onChange={e => {
                                    const page = e.target.value ? Number(e.target.value) - 1 : 0
                                    gotoPage(page)
                                }}
                                style={{ width: '75px' }}
                            />&nbsp;/&nbsp;{pageOptions.length}
                        </div>

                        <div onClick={() => (canNextPage) ? nextPage() : null} className="link p-right-10">
                            <span className="material-symbols-outlined" style={(!canNextPage) ? {opacity:0.8} : {}}>chevron_right</span>
                        </div>

                        <div onClick={() => (canNextPage) ? gotoPage(pageCount - 1) : null} className="link p-right-10">
                            <span className="material-symbols-outlined" style={(!canNextPage) ? {opacity:0.8} : {}}>keyboard_double_arrow_right</span>
                        </div>

                    </div>
                    )}
                </div>
                
            </div>
            )}

        </div>

    )
})

TableCustom.propTypes = {
    showHeader: bool,
    showFooter: bool,
    showDownloadCSV: bool,
    floatingDownloadCSVButton: bool,
    showFilterGlobal: bool,
    showPageSize: bool,
    showPageCountOnlyPage: bool,
    useSelectedField: bool,
    pageSize: number,
    pageIndex: number,
    restoredPageIndex: number,
    defaultGlobalFilter: string,
    className: string,
    messageEmpty: string,
    columns: array,
    data: array,
    subComponent: func,
    csvName: string,
    onClickRow: func,
    onItemsSelected: func,
    avmr: object,
    disabled: bool,
    selectionAllowed: func,
    selectionLabelAccessor: PropTypes.oneOfType([string, func]),
    getRowClassName: func,
};

TableCustom.defaultProps = {
    showHeader: true,
    showFooter: false,
    showDownloadCSV: true,
    floatingDownloadCSVButton: false,
    showFilterGlobal: true,
    showPageSize: true,
    showPageCountOnlyPage: false,
    useSelectedField: false,
    pageSize: 10,
    pageIndex: 0,
    className: "",
    messageEmpty: "No se encontraron resultados",
    columns: [],
    data: [],
    subComponent: null,
    csvName: 'datos',
    onClickRow: null,
    onItemsSelected: () => {},
    avmr: null,
    disabled: false,
    selectionAllowed: (row) => true,
    selectionLabelAccessor: null,
    getRowClassName: null,
};

export default TableCustom;
